import { Dialog, Transition } from '@headlessui/react';
import { useEffect, useState } from 'react';
import config from '../../../utils/config';
import logError from '../../../utils/errorUtil';
import { useAuth } from '../../context/wms-context';
import LoadingSpinner from '../../../components/LoadingSpinner';

export default function CreateNewPartModal({
    showNewPartModal,
    setShowNewPartModal,
    setNotificationMessage,
    setShowNotification,
    formData,
    newPartDefaultName,
}) {
    const { currentUser, setCurrentUser } = useAuth();
    const ORIGIN = config.origin;

    const [newPartNumber, setNewPartNumber] = useState(newPartDefaultName ?? '');
    const [newPartDescription, setNewPartDescription] = useState('');
    const [newPartKeyword, setNewPartKeyword] = useState('');
    const [keywordManuallyOverridden, setKeywordManuallyOverridden] = useState(false);

    const [showSpinner, setShowSpinner] = useState(false);

    const handlePartDescriptionChange = (value) => {
        setNewPartDescription(value);
        // Auto-populate keyword with first 15 characters of description only if not manually overridden
        if (!keywordManuallyOverridden) {
            setNewPartKeyword(value.substring(0, 15));
        }
    };

    const handleKeywordChange = (value) => {
        const trimmedValue = value.substring(0, 15);
        setNewPartKeyword(trimmedValue);

        // Check if this change represents a manual override
        // If the new keyword doesn't match what would be auto-generated, mark as overridden
        const autoGeneratedKeyword = newPartDescription.substring(0, 15);
        if (trimmedValue !== autoGeneratedKeyword) {
            setKeywordManuallyOverridden(true);
        } else {
            // If user manually changes it back to match auto-generated, allow auto-population again
            setKeywordManuallyOverridden(false);
        }
    };

    const createPart = async () => {
        setShowSpinner(true);

        const dataToSend = {
            partNumber: newPartNumber,
            partDescription: newPartDescription,
            partKeyword: newPartKeyword,
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
        };

        try {
            const response = await fetch(`${ORIGIN}/wms/receiving/create-part`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`Error creating part: ${data.message || response.statusText}`);
            }

            let newFormData = formData;
            newFormData[2] = {
                ...newFormData[2],
                value: data.partId,
                valueName: data.partName,
            };

            setNotificationMessage({
                title: 'Part Created',
                desc: `Part ${newPartNumber} has been created successfully.`,
                status: 'success',
            });
            setShowNotification(true);
            setTimeout(() => {
                setShowNotification(false);
            }, 5000);

            resetPartForm();
            setShowNewPartModal(false);
        } catch (error) {
            logError(error, 'createPart');

            setNotificationMessage({
                title: 'Error Creating Part',
                desc: error.message || 'An unexpected error occurred while creating the part.',
                status: 'error',
            });
            setShowNotification(true);
            setTimeout(() => {
                setShowNotification(false);
            }, 5000);

            setShowSpinner(false);
        }
    };

    const requiredFieldsEmpty = !newPartNumber.trim() || !newPartDescription.trim() || !newPartKeyword.trim();

    const resetPartForm = () => {
        setShowSpinner(false);
        setNewPartNumber('');
        setNewPartDescription('');
        setNewPartKeyword('');
        setKeywordManuallyOverridden(false);
    };

    useEffect(() => {
        setNewPartNumber(newPartDefaultName);
    }, [newPartDefaultName]);

    return (
        <Transition show={showNewPartModal}>
            <Dialog
                className='overflow-visible relative z-10'
                onClose={() => {
                    resetPartForm();
                    setShowNewPartModal(false);
                }}
            >
                <Transition.Child>
                    <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
                </Transition.Child>

                <div className='overflow-y-auto fixed inset-0 z-10'>
                    <div className='flex justify-center items-end p-4 min-h-full text-center sm:items-center sm:p-0'>
                        <Dialog.Panel className='flex overflow-visible relative flex-col justify-center items-center text-left bg-white rounded-lg shadow-xl transition-all transform sm:my-8 sm:w-1/5 sm:max-w-1/5 sm:p-8'>
                            {showSpinner ? (
                                <div className='flex flex-col gap-4 justify-around items-center w-full h-full'>
                                    <h2 className='mt-4 mb-6 text-2xl font-bold'>Create New Part</h2>
                                    <LoadingSpinner className={'mt-0'} />
                                </div>
                            ) : (
                                <div className='flex flex-col gap-4 justify-around items-center w-full h-full'>
                                    <h2 className='mt-4 mb-6 text-2xl font-bold'>Create New Part</h2>
                                    <label className='self-start text-sm font-medium'>Part Number</label>
                                    <input
                                        type='text'
                                        placeholder='Part Number'
                                        className='w-full'
                                        value={newPartNumber}
                                        onChange={(e) => setNewPartNumber(e.target.value)}
                                    />
                                    <label className='self-start text-sm font-medium'>Part Description</label>
                                    <input
                                        type='text'
                                        placeholder='Part Description'
                                        className='w-full'
                                        value={newPartDescription}
                                        onChange={(e) => handlePartDescriptionChange(e.target.value)}
                                    />
                                    <label className='self-start text-sm font-medium'>Keyword</label>
                                    <input
                                        type='text'
                                        placeholder='Keyword'
                                        className='w-full'
                                        value={newPartKeyword}
                                        onChange={(e) => handleKeywordChange(e.target.value)}
                                    />
                                    <div className='flex justify-end w-full'>
                                        <button
                                            className='inline-flex justify-center px-3 py-2 mt-3 w-full text-sm font-semibold text-gray-900 bg-white rounded-md ring-1 ring-inset ring-gray-300 shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto'
                                            onClick={() => {
                                                resetPartForm();
                                                setShowNewPartModal(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={`inline-flex justify-center px-3 py-2 w-full text-sm font-semibold text-white rounded-md shadow-sm bg-blue-primary sm:ml-3 sm:w-auto ${
                                                requiredFieldsEmpty ? 'cursor-default' : 'hover:bg-blue-secondary '
                                            }`}
                                            disabled={requiredFieldsEmpty}
                                            onClick={() => {
                                                createPart();
                                            }}
                                        >
                                            Create
                                        </button>
                                    </div>
                                </div>
                            )}
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
